/**
 * Evaluator-Led Outline View Component
 * Displays hierarchical RFP structure with sections, requirements, and criteria
 * Designed for evaluators to understand RFP organization
 */
import React, { useState, useMemo } from 'react';
import { Card, Input, Button } from '@tracekit/core';
import { Search, ChevronDown, ChevronRight, CheckCircle, Star, Download } from 'lucide-react';
import type { RfpSection, RfpRequirement, RfpEvaluationCriterion, RfpStructureMetadata } from '../../types/rfp';

export interface EvaluatorLedOutlineViewProps {
  // V3 structure (NEW - priority)
  structure?: RfpStructureMetadata;
  
  // Legacy props (for backward compatibility)
  sections?: RfpSection[];
  requirements?: RfpRequirement[];
  criteria?: RfpEvaluationCriterion[];
  outline?: any;
  
  qualityScore?: number;
  onSectionClick?: (section: RfpSection) => void;
  loading?: boolean;
}

export function EvaluatorLedOutlineView({
  structure,
  sections: legacySections,
  requirements: legacyRequirements,
  criteria: legacyCriteria,
  outline,
  qualityScore,
  onSectionClick,
  loading = false,
}: EvaluatorLedOutlineViewProps) {
  // Merge V3 and legacy data
  const sections = structure?.sections || legacySections || [];
  const requirements = structure?.requirements || legacyRequirements || [];
  const criteria = structure?.evaluation_criteria || legacyCriteria || [];
  const quality = structure?.quality_metrics?.quality_score || qualityScore || 0;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Build hierarchical tree
  const tree = useMemo(() => {
    const sectionsById = new Map(sections.map((s) => [s.id, s]));
    const rootSections: RfpSection[] = [];
    const childrenMap = new Map<string, RfpSection[]>();

    sections.forEach((section) => {
      if (!section.parent_id || !sectionsById.has(section.parent_id)) {
        rootSections.push(section);
      } else {
        if (!childrenMap.has(section.parent_id)) {
          childrenMap.set(section.parent_id, []);
        }
        childrenMap.get(section.parent_id)!.push(section);
      }
    });

    // Sort children by number
    childrenMap.forEach((children) => {
      children.sort((a, b) => {
        const aNum = a.number.split('.').map(Number);
        const bNum = b.number.split('.').map(Number);
        for (let i = 0; i < Math.max(aNum.length, bNum.length); i++) {
          if ((aNum[i] || 0) !== (bNum[i] || 0)) {
            return (aNum[i] || 0) - (bNum[i] || 0);
          }
        }
        return 0;
      });
    });

    rootSections.sort((a, b) => {
      const aNum = parseFloat(a.number);
      const bNum = parseFloat(b.number);
      return aNum - bNum;
    });

    return { rootSections, childrenMap };
  }, [sections]);

  // Filter sections by search
  const filteredSectionIds = useMemo(() => {
    if (!searchQuery) return new Set(sections.map((s) => s.id));

    const query = searchQuery.toLowerCase();
    const matching = new Set<string>();

    sections.forEach((section) => {
      if (
        section.number.toLowerCase().includes(query) ||
        section.title.toLowerCase().includes(query) ||
        section.content.toLowerCase().includes(query)
      ) {
        matching.add(section.id);
        // Also add all parents
        let currentId = section.parent_id;
        while (currentId) {
          matching.add(currentId);
          const parent = sections.find((s) => s.id === currentId);
          currentId = parent?.parent_id ?? null;
        }
      }
    });

    return matching;
  }, [sections, searchQuery]);

  // Count requirements and criteria per section
  const sectionStats = useMemo(() => {
    const stats = new Map<string, { requirements: number; criteria: number }>();

    requirements.forEach((req) => {
      if (req.section_id) {
        const current = stats.get(req.section_id) || { requirements: 0, criteria: 0 };
        current.requirements++;
        stats.set(req.section_id, current);
      }
    });

    criteria.forEach((crit) => {
      if (crit.section_id) {
        const current = stats.get(crit.section_id) || { requirements: 0, criteria: 0 };
        current.criteria++;
        stats.set(crit.section_id, current);
      }
    });

    return stats;
  }, [requirements, criteria]);

  const toggleExpanded = (sectionId: string) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpanded(newExpanded);
  };

  const expandAll = () => {
    setExpanded(new Set(sections.map((s) => s.id)));
  };

  const collapseAll = () => {
    setExpanded(new Set());
  };

  // Export functions
  const exportAsJSON = () => {
    const data = { sections, requirements, criteria, qualityScore: quality };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfp-structure-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = () => {
    const csvRows = ['Level,Number,Title,Page,Requirements,Criteria'];
    sections.forEach(s => {
      const stats = sectionStats.get(s.id);
      const row = [
        s.hierarchy_level,
        `"${s.number}"`,
        `"${s.title}"`,
        s.page || '',
        stats?.requirements || 0,
        stats?.criteria || 0
      ].join(',');
      csvRows.push(row);
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfp-structure-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderSection = (section: RfpSection, level: number = 0): React.ReactNode => {
    if (!filteredSectionIds.has(section.id)) return null;

    const children = tree.childrenMap.get(section.id) || [];
    const stats = sectionStats.get(section.id);
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(section.id);

    return (
      <div key={section.id} className="border-l-2 border-gray-200 dark:border-gray-700 ml-4">
        <div
          className="flex items-center gap-2 py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          onClick={() => {
            if (hasChildren) toggleExpanded(section.id);
            if (onSectionClick) onSectionClick(section);
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
            )
          ) : (
            <div className="w-4" />
          )}
          
          <span className={`text-sm ${level === 0 ? 'font-bold' : 'font-medium'} text-gray-700 dark:text-gray-300`}>
            {section.number}
          </span>
          
          <span className={`text-sm ${level === 0 ? 'font-bold' : ''} text-gray-900 dark:text-theme-text flex-1`}>
            {section.title}
          </span>

          {section.page && (
            <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800">
              Page {section.page}
            </span>
          )}

          {stats && (
            <div className="flex items-center gap-2">
              {stats.requirements > 0 && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs">
                  <CheckCircle className="h-3 w-3" />
                  <span>{stats.requirements}</span>
                </div>
              )}
              {stats.criteria > 0 && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 text-xs">
                  <Star className="h-3 w-3" />
                  <span>{stats.criteria}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {isExpanded && children.length > 0 && (
          <div className="ml-0">
            {children.map((child) => renderSection(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">Loading RFP structure...</p>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No RFP structure extracted. Please run extraction first or upload RFP documents.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">RFP Structure Outline</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {sections.length} sections • {requirements.length} requirements • {criteria.length} criteria
            {quality > 0 && <span className="ml-2">• Quality: {quality}/100</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
          <Button variant="outline" size="sm" onClick={exportAsJSON}>
            <Download className="h-4 w-4 mr-1" />
            JSON
          </Button>
          <Button variant="outline" size="sm" onClick={exportAsCSV}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search sections, titles, or content..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Top-Level Sections</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-theme-text">{tree.rootSections.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Sections</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-theme-text">{sections.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Max Depth</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-theme-text">
            {Math.max(...sections.map((s) => s.hierarchy_level))}
          </p>
        </Card>
      </div>

      {/* Tree View */}
      <Card className="p-4 max-h-[600px] overflow-y-auto">
        {tree.rootSections.map((section) => renderSection(section, 0))}
      </Card>

      {filteredSectionIds.size === 0 && searchQuery && (
        <Card className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            No sections match your search. Try a different query.
          </p>
        </Card>
      )}
    </div>
  );
}
